import { CacheManager } from 'src/cacheManager';
import { ClientConfiguration } from 'src/clientConfiguration';
import { DataManager } from 'src/kameleoonData';
import {
  KameleoonDataType,
  KameleoonVisitorDataType,
  VisitDataType,
} from 'src/kameleoonData/types';
import { Requester } from 'src/requester';
import { GetVisitorDataResultType } from 'src/requester/types';
import { SegmentType } from 'src/targeting';
import { TrackingCacheItemType } from 'src/types';
import { ExternalPackageInfoType } from 'src/sdkInfoTypes';
import { VariationConfiguration } from 'src/variationConfiguration';
import { MappingIdentifierType } from 'src/storage/types';

export type CheckTargetingParametersType = {
  segment: SegmentType | null;
  experimentId: number | null;
  visitorCode: string;
  packageInfo: ExternalPackageInfoType;
  targetingData?: KameleoonVisitorDataType;
  clientConfiguration: ClientConfiguration;
  variationConfiguration?: VariationConfiguration;
  dataManager: DataManager;
};

export enum TimeUnit {
  Minute = 'MINUTE',
  Hour = 'HOUR',
  Day = 'DAY',
  Week = 'WEEK',
  Month = 'MONTH',
}

export enum Order {
  Ascending = 'asc',
  Descending = 'desc',
}

export type InsertInOrderedListParametersType = {
  list: number[];
  element: number;
  order: Order;
};

export type InsertVisitInOrderedListParametersType = {
  list: VisitDataType[];
  visit: VisitDataType;
};

export type ParseVisitorDataParametersType = {
  data: GetVisitorDataResultType;
  filters: VisitorDataFiltersType;
  visitorCode: string;
  dataManager: DataManager;
  variationConfiguration: VariationConfiguration;
};

export type ParseVisitorDataResultType = {
  storageVisitorData: KameleoonDataType[];
  visitorData: KameleoonDataType[];
  visitsData?: KameleoonDataType;
  mappingIdentifier?: MappingIdentifierType;
};

export type ManageCacheParametersType = {
  hasJsCssVariables: boolean;
  cacheManager: CacheManager<TrackingCacheItemType>;
  visitorCode: string;
  experimentId: number;
  variationId: number;
};

export type TrackDataParametersType = {
  visitorCode: string;
  dataManager: DataManager;
  requester: Requester;
  isConsentProvided: boolean;
  isOfflineModeOn?: boolean;
  isMappingIdentifier?: boolean;
};

export type TrackExperimentParametersType = {
  clientConfiguration: ClientConfiguration;
  visitorCode: string;
  requester: Requester;
  variationId: number;
  experimentId: number;
  isUnallocated: boolean;
  isOfflineModeOn?: boolean;
};

/**
 * @param {number} previousVisitAmount - Number of previous visits to retrieve data from. Number between `1` and `25`
 * *Default*: `1`.
 * @param {number} currentVisit - If true, current visit data will be retrieved.
 * *Default*: `true`.
 * @param {boolean} customData - If true, custom data will be retrieved.
 * *Default*: `true`.
 * @param {boolean} pageViews - If true, page data will be retrieved.
 * *Default*: `false`.
 * @param {boolean} geolocation - If true, geolocation data will be retrieved.
 * *Default*: `false`.
 * @param {boolean} device - If true, device data will be retrieved.
 * *Default*: `false`.
 * @param {boolean} browser - If true, browser data will be retrieved.
 * *Default*: `false`.
 * @param {boolean} operatingSystem - If true, operating system data will be retrieved.
 * *Default*: `false`.
 * @param {boolean} conversions - If true, conversion data will be retrieved.
 * *Default*: `false`.
 * @param {boolean} experiments - If true, experiment data will be retrieved.
 * *Default*: `false`.
 * @param {boolean} kcs - If true, Kameleoon Conversion Score data will be retrieved.
 * *Default*: `false`.
 * @param {boolean} visitorCode - If true, visitor code will be retrieved.
 * *Default*: `true`.
 * @param {boolean} personalization - If true, personalization data will be retrieved.
 * *Default*: `false`.
 * @param {boolean} cbs - If true, Contextual Bandit evaluations data will be retrieved.
 * *Default*: `false`.
 * */
export type VisitorDataFiltersType = {
  previousVisitAmount?: number;
  currentVisit?: boolean;
  pageViews?: boolean;
  geolocation?: boolean;
  device?: boolean;
  browser?: boolean;
  operatingSystem?: boolean;
  customData?: boolean;
  conversions?: boolean;
  experiments?: boolean;
  kcs?: boolean;
  visitorCode?: boolean;
  personalization?: boolean;
  cbs?: boolean;
};

/**
 * @interface `IExternalPRNG` - an interface for external PRNG (Pseudo-Random Number Generator) used to generate secure random numbers depending on the environment and platform
 *  */
export interface IExternalPRNG {
  /**
   * @method getRandomNumber - a method to generate a secure random number
   * @returns {number} a secure random `number`, which must be a floating-point number between 0 and 1, similarly to `Math.random()`
   * */
  getRandomNumber(): number;
}
